// Terms of Service content for SureDox
// Last updated: February 2025

export const termsOfServiceContent = `
<h1>SureDox Terms of Service</h1>

<p class="effective-date"><strong>Effective Date:</strong> 1 February 2025<br />
<strong>Last Updated:</strong> 1 February 2025</p>

<hr />

<h2>1. Introduction</h2>

<p>These Terms of Service ("Terms") govern your use of the SureDox document redaction platform ("Service") operated by Boone and Boo (Pty) Ltd, trading as SureDox ("SureDox", "we", "us", or "our").</p>

<p>By creating an account or using our Service, you agree to be bound by these Terms. If you are using the Service on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms.</p>

<hr />

<h2>2. Service Description</h2>

<p>SureDox provides an AI-powered document redaction service that:</p>

<ul>
<li>Accepts PDF documents uploaded by users</li>
<li>Automatically detects personally identifiable information (PII) using artificial intelligence</li>
<li>Allows users to review, edit, and confirm redactions</li>
<li>Produces redacted documents for download</li>
</ul>

<p>The Service is designed to assist with compliance with the Protection of Personal Information Act 4 of 2013 (POPIA) and similar data protection requirements.</p>

<hr />

<h2>3. Account Registration</h2>

<p>To use the Service, you must:</p>

<ul>
<li>Create an account with accurate and complete information</li>
<li>Maintain the security of your login credentials</li>
<li>Notify us immediately of any unauthorised access</li>
<li>Be at least 18 years old or have legal capacity to enter contracts</li>
</ul>

<p>You are responsible for all activities that occur under your account.</p>

<hr />

<h2>4. Your Responsibilities</h2>

<h3>4.1 Document Ownership and Authority</h3>

<p>You represent and warrant that:</p>

<ul>
<li>You own or have legal authority to process all documents you upload</li>
<li>You have obtained any necessary consents from data subjects whose personal information appears in the documents</li>
<li>Your use of the Service complies with all applicable laws, including POPIA</li>
</ul>

<h3>4.2 Prohibited Uses</h3>

<p>You agree not to:</p>

<ul>
<li>Upload documents containing illegal content</li>
<li>Use the Service to facilitate unlawful processing of personal information</li>
<li>Attempt to circumvent security measures</li>
<li>Reverse engineer or copy the Service</li>
<li>Resell or redistribute the Service without authorisation</li>
<li>Upload malicious files or code</li>
</ul>

<h3>4.3 Your Role as Responsible Party</h3>

<p>Under POPIA, you are the "Responsible Party" for any personal information contained in documents you upload. You determine the purpose and means of processing. SureDox acts solely as your "Operator" - we process personal information only to provide the redaction service you have requested.</p>

<hr />

<h2>5. Data Processing Terms</h2>

<h3>5.1 Our Role as Operator</h3>

<p>In terms of POPIA, SureDox acts as an Operator processing personal information on your behalf. We:</p>

<ul>
<li>Process documents only according to your instructions (i.e., to perform redaction)</li>
<li>Do not use document contents for any other purpose</li>
<li>Do not determine the purposes of processing - you do</li>
<li>Implement appropriate security measures as required by Section 19 of POPIA</li>
</ul>

<h3>5.2 Processing Activities</h3>

<p>When you upload a document, we:</p>

<ol>
<li>Store it securely in your isolated workspace (encrypted at rest)</li>
<li>Transmit it to our AI provider for PII detection (encrypted in transit)</li>
<li>Return detection results for your review</li>
<li>Apply your confirmed redactions</li>
<li>Make the redacted document available for download</li>
<li>Automatically delete the document after the retention period</li>
</ol>

<h3>5.3 Sub-processors</h3>

<p>We use the following third-party service providers to deliver the Service:</p>

<table>
<thead>
<tr><th>Provider</th><th>Purpose</th><th>Location</th></tr>
</thead>
<tbody>
<tr><td>Google Cloud Platform / Firebase</td><td>Hosting, storage, authentication</td><td>EU/US</td></tr>
<tr><td>Anthropic (Claude API)</td><td>AI-powered PII detection</td><td>United States</td></tr>
<tr><td>DocuPipe</td><td>Image-based PDF processing</td><td>United States (New York)</td></tr>
<tr><td>Paystack</td><td>Payment processing</td><td>South Africa</td></tr>
</tbody>
</table>

<p>By accepting these Terms, you authorise our use of these sub-processors. We ensure all sub-processors are bound by appropriate data protection obligations.</p>

<h3>5.4 AI and Machine Learning</h3>

<p><strong>Your documents are not used for AI training.</strong> Our AI provider (Anthropic) operates under a zero-retention policy for commercial API usage. Documents are processed and immediately discarded - they are never stored, reviewed by humans, or used for model training.</p>

<h3>5.5 Data Retention and Deletion</h3>

<p>We retain data for the following periods:</p>

<ul>
<li><strong>Uploaded and processed documents, including PII detection metadata:</strong> 7 days from processing, then permanently deleted</li>
<li><strong>Transaction and billing records:</strong> 7 years (legal requirement)</li>
<li><strong>Account information:</strong> Duration of account plus 30 days after deletion</li>
</ul>

<p>You may manually delete documents at any time before automatic deletion.</p>

<h3>5.6 Cross-Border Transfers</h3>

<p>Some of our sub-processors are located outside South Africa. By using the Service, you acknowledge that your documents may be transferred to countries including the United States. We ensure such transfers comply with Section 72 of POPIA through appropriate safeguards including standard contractual clauses and reliance on adequate data protection frameworks.</p>

<h3>5.7 Security Measures</h3>

<p>We implement appropriate technical and organisational measures including:</p>

<ul>
<li>Encryption in transit (TLS 1.2+) and at rest (AES-256)</li>
<li>Access controls and authentication</li>
<li>Isolated storage per user</li>
<li>Audit logging</li>
<li>Regular security assessments</li>
</ul>

<p>Full details are available in our Security Safeguards documentation, available on request.</p>

<h3>5.8 Security Incidents</h3>

<p>If we become aware of a security breach affecting your data, we will:</p>

<ul>
<li>Notify you within 72 hours</li>
<li>Provide details of the nature and scope of the breach</li>
<li>Describe remedial measures taken</li>
<li>Cooperate with your obligations to notify the Information Regulator and affected parties</li>
</ul>

<hr />

<h2>6. Credits and Payment</h2>

<h3>6.1 Credit System</h3>

<p>The Service operates on a credit-based model:</p>

<ul>
<li>Text-based PDF pages: 1 credit per page</li>
<li>Image-based/scanned PDF pages: 5 credits per page</li>
<li>Credits are deducted upon processing confirmation</li>
</ul>

<h3>6.2 Pricing</h3>

<p>Credit pricing is displayed on our website. We may change pricing with 30 days notice. Purchased credits do not expire.</p>

<h3>6.3 Payment</h3>

<p>Payments are processed by Paystack in South African Rand (ZAR). You agree to provide accurate billing information. All fees are exclusive of VAT unless stated otherwise.</p>

<h3>6.4 Refunds</h3>

<h4>6.4.1 Unused Credits</h4>

<p>You may request a full refund for unused credits within <strong>48 hours</strong> of purchase. To request a refund, contact us at <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a> with your account details and proof of purchase.</p>

<p>Refund requests for unused credits made after 48 hours may be considered on a case-by-case basis at SureDox's sole discretion. Such requests should include the reason for the refund and any relevant circumstances.</p>

<h4>6.4.2 Used Credits - Technical Issues</h4>

<p>If you experience technical issues with the Service that prevent successful document processing, you may request a refund or credit restoration by:</p>

<ol>
<li>Contacting us at <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a> within <strong>14 days</strong> of the issue occurring</li>
<li>Providing details of the issue, including document name(s), date of processing, and description of the problem</li>
<li>Allowing us reasonable time to investigate the issue</li>
</ol>

<p>Upon investigation, if we determine that credits were consumed due to a technical fault on our part, we will, at our discretion, either:</p>

<ul>
<li>Restore the affected credits to your account; or</li>
<li>Process a refund to your original payment method</li>
</ul>

<h4>6.4.3 No Refunds</h4>

<p>Refunds will not be provided where:</p>

<ul>
<li>Credits were successfully used to process documents as intended</li>
<li>The issue arose from documents that were corrupted, password-protected, or otherwise unsuitable for processing</li>
<li>The request is made outside the timeframes specified above (unless exceptional circumstances apply)</li>
<li>The account has been terminated for breach of these Terms</li>
</ul>

<h4>6.4.4 Consumer Protection Act</h4>

<p>Nothing in this refund policy limits any rights you may have under the Consumer Protection Act 68 of 2008, where applicable.</p>

<hr />

<h2>7. Intellectual Property</h2>

<h3>7.1 Our Intellectual Property</h3>

<p>The Service, including software, algorithms, designs, and documentation, is owned by SureDox or our licensors. These Terms do not grant you any rights to our intellectual property except the limited right to use the Service.</p>

<h3>7.2 Your Content</h3>

<p>You retain ownership of all documents you upload. By uploading documents, you grant us a limited licence to process them solely for providing the Service.</p>

<hr />

<h2>8. Disclaimers</h2>

<h3>8.1 Service Provided "As Is"</h3>

<p>The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>

<h3>8.2 No Legal Advice</h3>

<p>SureDox is a technical tool, not a legal service. We do not guarantee that use of the Service will ensure legal compliance. You are responsible for determining your legal obligations and whether the Service meets your requirements.</p>

<h3>8.3 AI Limitations</h3>

<p>While our AI technology is designed to detect PII accurately, it may not identify all personal information in every document. You are responsible for reviewing and confirming all redactions before finalising documents.</p>

<hr />

<h2>9. Limitation of Liability</h2>

<h3>9.1 Exclusion of Damages</h3>

<p>To the maximum extent permitted by law, SureDox shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.</p>

<h3>9.2 Liability Cap</h3>

<p>Our total liability for any claims arising from these Terms or your use of the Service shall not exceed the fees paid by you in the 12 months preceding the claim.</p>

<h3>9.3 Exceptions</h3>

<p>Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, or any liability that cannot be excluded by law.</p>

<hr />

<h2>10. Indemnification</h2>

<p>You agree to indemnify and hold harmless SureDox, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:</p>

<ul>
<li>Your use of the Service</li>
<li>Your violation of these Terms</li>
<li>Your violation of any third-party rights</li>
<li>Documents you upload to the Service</li>
</ul>

<hr />

<h2>11. Term and Termination</h2>

<h3>11.1 Term</h3>

<p>These Terms commence when you create an account and continue until terminated.</p>

<h3>11.2 Termination by You</h3>

<p>You may terminate your account at any time by contacting us. Upon termination, your documents will be deleted according to our retention schedule.</p>

<h3>11.3 Termination by Us</h3>

<p>We may suspend or terminate your account if you breach these Terms or if required by law. We will provide reasonable notice where possible.</p>

<h3>11.4 Effect of Termination</h3>

<p>Upon termination, your right to use the Service ceases. Sections that by their nature should survive (including liability limitations, indemnification, and governing law) will continue in effect.</p>

<hr />

<h2>12. Changes to Terms</h2>

<p>We may update these Terms from time to time. We will notify you of material changes by email or through the Service. Continued use after changes take effect constitutes acceptance of the new Terms.</p>

<hr />

<h2>13. General Provisions</h2>

<h3>13.1 Governing Law</h3>

<p>These Terms are governed by the laws of the Republic of South Africa. Any disputes shall be subject to the exclusive jurisdiction of the South African courts.</p>

<h3>13.2 Entire Agreement</h3>

<p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and SureDox regarding the Service.</p>

<h3>13.3 Severability</h3>

<p>If any provision of these Terms is found invalid or unenforceable, the remaining provisions will continue in effect.</p>

<h3>13.4 Waiver</h3>

<p>Our failure to enforce any right or provision does not constitute a waiver of that right or provision.</p>

<h3>13.5 Assignment</h3>

<p>You may not assign these Terms without our consent. We may assign our rights and obligations without restriction.</p>

<hr />

<h2>14. Contact Information</h2>

<p>For questions about these Terms:</p>

<p><strong>Boone and Boo (Pty) Ltd</strong> (trading as SureDox)<br />
Registration Number: 2025 / 489700 / 07<br />
Address: 9 Clarence Avenue, Craighall Park, Johannesburg<br />
Email: <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a></p>

<p>For data protection queries:<br />
Information Officer: Eben Smit<br />
Email: <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a></p>

<hr />

<h2>15. POPIA Rights</h2>

<p>You have rights under POPIA regarding personal information we hold about you (as distinct from documents you upload). See our <a href="/privacy">Privacy Policy</a> for details on how to exercise these rights.</p>

<hr />

<p class="agreement-notice"><em>By creating an account or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</em></p>
`;

export default termsOfServiceContent;
